import React, { useState, useEffect } from 'react';
import { ReactComponent as EmbotelladoIcon } from '../../assets/icons/EmbotelladoIcon.svg';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, 
Combobox,
ComboboxInput,
ComboboxButton,
ComboboxOptions,
ComboboxOption } from '@headlessui/react';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { invalidarCacheColecciones } from '../../services/dataCache';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import FechaDateInput from '../FechaDateInput';

// MODIFICACIÓN: Se agrega 'Crema de Estrellas' a la lista de tipos de vino
const tiposDeVino = [
'White',
'Red',
'White Blend',
'Red Blend',
'Crema de Estrellas' 
];

const tiposDeMedida = [
    'Piccolo (187,5 ml)',
    'Media Botella (375 ml)',
    'Estándar (750 ml)',
    'Magnum (1.5 L)',
    'Doble Magnum (3 L)',
    'Jeroboam (Vino) (4.5 L)',
    'Jeroboam (Champán) (3 L)',
    'Imperial (6 L)',
    'Salmanazar (9 L)',
    'Baltasar (12 L)',
    'Nabucodonosor (15 L)',
    'Salomón (18 L)',
    'Melchor (20 L)',
    'Souverain (26.25 L)',
    'Primat (27 L)'
];
// El dato se guarda siempre en hectolitros; estas unidades son solo para mostrar/cargar.
const unidadesVolumen = ['Hectolitros', 'Litros'];
const abreviaturasVolumen = { Hectolitros: 'hl', Litros: 'lts' };
const estadoInicial = {
pertenencia: '',
tipo_uso: '',
nombre_empresa: '',
profesional_responsable: '',
tipo_vino: '',
partidas_usadas: [{ origen: 'vinificacion', partida_id: '', cantidad_hl: '' }],
embotellados: [{ forma_botella: '', medida_botella: '', tipo_corcho: '', cantidad_botellas: '' }],
estiba_inicio: '',
estiba_final: '',
// Sólo se usa si TODO el vino es comprado. Si hay al menos una partida propia,
// el año sale de la cosecha de su lote y este campo ni se muestra.
anada: ''
};
const opcionesTipoUso = ['Propio', 'Marca Blanca'];

const EmbotelladoForm = () => {
const navigate = useNavigate();
const [formData, setFormData] = useState(estadoInicial);
const [unidadVolumen, setUnidadVolumen] = useState('Hectolitros');
const [partidas, setPartidas] = useState([]);
// Vino a granel comprado, botellas y tapones: viven en stock, categoría
// Insumos. El vino comprado no tiene documento de vinificación detrás, así
// que la añada se carga a mano y la cepa se saca del nombre del artículo.
const [insumos, setInsumos] = useState([]);
const [proveedores, setProveedores] = useState({});
const [message, setMessage] = useState(null);
const [isLoading, setIsLoading] = useState(false);
const [progress, setProgress] = useState(0);
const [query, setQuery] = useState('');
useEffect(() => {
const fetchPartidas = async () => {
try {
const response = await fetch('https://backend-288175208699.europe-west1.run.app/api/partidas', {
headers: { Authorization: `Bearer ${await getAuth().currentUser.getIdToken()}` },
});
if (!response.ok) {
throw new Error('No se pudo obtener la lista de partidas.');
}
const data = await response.json();
setPartidas(data);
} catch (error) {
console.error("Error al cargar partidas:", error);
setMessage({ type: 'error', text: error.message });
} finally {
}
};
fetchPartidas();
}, []);
useEffect(() => {
const fetchInsumos = async () => {
try {
const [snapStock, snapProv] = await Promise.all([
getDocs(collection(db, 'stock')),
getDocs(collection(db, 'proveedores')),
]);
// Sólo lo comprado que esté en Insumos y tenga stock disponible.
// Los DEMO-* son datos de prueba del sitio público, no stock real.
const items = snapStock.docs
.filter(d => !d.id.startsWith('DEMO-'))
.map(d => ({ id: d.id, ...d.data() }))
.filter(i => i.categoria === 'Insumos' && Number(i.cantidadUnidades) > 0);
const provs = {};
snapProv.docs.forEach(d => { provs[d.id] = d.data(); });
setInsumos(items);
setProveedores(provs);
} catch (error) {
console.error('Error al cargar insumos de stock:', error);
}
};
fetchInsumos();
}, []);

// El stock puede estar medido en litros (así entra desde una compra) o en
// hectolitros. El formulario habla en hl, así que se traduce lo disponible.
const insumoAHectolitros = (insumo) => {
const cant = Number(insumo?.cantidadUnidades) || 0;
return insumo?.tipoUnidad === 'Litro' ? cant / 100 : cant;
};
const buscarInsumo = (id) => insumos.find(i => i.id === id);
// Una misma botella o tapón puede repetirse en varias líneas (distintos
// tipos de vino embotellados con el mismo envase). Esto suma lo pedido en
// TODAS las líneas para un mismo ítem, para poder avisar si entre todas se
// pasan del stock disponible.
const totalPedidoDeItem = (id) => formData.embotellados.reduce((total, b) => {
const cantidad = parseInt(b.cantidad_botellas) || 0;
if (cantidad <= 0) return total;
let suma = total;
if (b.forma_botella === id) suma += cantidad;
if (b.tipo_corcho === id) suma += cantidad;
return suma;
}, 0);
// El año se pide sólo si no hay ninguna partida propia de dónde sacarlo.
const todoComprado = formData.partidas_usadas.length > 0 &&
formData.partidas_usadas.every(p => (p.origen || 'vinificacion') === 'stock' && p.partida_id);
// Si alguna partida ya viene embotellada de fábrica, no hay botella ni
// tapón que elegir para toda la tirada: no se van a mezclar una partida ya
// embotellada con otra que sí hay que embotellar acá.
const hayPartidaEmbotellada = formData.partidas_usadas.some(
p => (p.origen || 'vinificacion') === 'stock' && p.ya_embotellado
);
// Cantidad total de botellas ya embotelladas cargada en la columna de la
// derecha: es lo que se descuenta del stock de la partida comprada.
const totalBotellasCompradas = formData.embotellados.reduce(
(total, b) => total + (parseInt(b.cantidad_botellas) || 0), 0
);

// Botellas, tapones y vino a granel comprado viven todos en 'stock' (Insumos),
// distinguidos por cómo Leandro nombra el artículo al cargar la compra.
// Se confía en que esa convención se respete: "Botella ...", "Tapón ..." /
// "Corcho ..." (el nombre viejo, por si queda stock cargado así) y "Vino ...".
// Se usa el campo 'nombre' (no el id del documento) para filtrar y mostrar:
// si dos proveedores distintos venden algo con el mismo nombre, el backend
// desambigua el ID del documento agregando el proveedor, pero 'nombre' se
// mantiene limpio ("Vino Malbec", sin sufijo).
const nombreInsumo = (i) => i?.nombre || i?.id || '';
const empiezaCon = (texto, prefijo) => (texto || '').trim().toLowerCase().startsWith(prefijo.toLowerCase());
const vinosComprados = insumos.filter(i => empiezaCon(nombreInsumo(i), 'Vino'));
const botellasDisponibles = insumos.filter(i => empiezaCon(nombreInsumo(i), 'Botella'));
const taponesDisponibles = insumos.filter(i => empiezaCon(nombreInsumo(i), 'Tapón') || empiezaCon(nombreInsumo(i), 'Tapon') || empiezaCon(nombreInsumo(i), 'Corcho'));
// "Vino Malbec" -> cepa "Malbec". Si no matchea el patrón esperado, se deja
// el nombre completo como cepa antes que perder el dato.
const cepaDesdeNombreVino = (i) => {
const texto = nombreInsumo(i);
const partes = texto.trim().split(/\s+/);
if (partes.length > 1 && partes[0].toLowerCase() === 'vino') {
return partes.slice(1).join(' ');
}
return texto;
};
const handleChange = (e, campo, value) => {
const newValue = value !== undefined ? value : e.target.value;
setFormData(prev => ({
...prev,
[campo]: newValue
}));
};
const handleSubmit = async (e) => {
e.preventDefault();
setIsLoading(true);
setProgress(0);
setMessage(null);
// Se mantiene la validación para 'White Blend' y 'Red Blend'
if ((formData.tipo_vino === 'White Blend' || formData.tipo_vino === 'Red Blend') && formData.partidas_usadas.length < 2) {
setMessage({ type: 'error', text: 'Para los tipos de vino "White Blend" o "Red Blend", debes seleccionar al menos dos partidas.' });
setIsLoading(false);
return;
}
const user = getAuth().currentUser;
if (!user) {
setMessage({ type: 'error', text: 'Debes iniciar sesión para registrar el embotellado.' });
setIsLoading(false);
return;
}
try {
const idToken = await user.getIdToken();
const datosParaEnviar = { ...formData };
const interval = setInterval(() => {
setProgress(prev => Math.min(prev + 5, 95)); 
}, 100);
const response = await fetch('https://backend-288175208699.europe-west1.run.app/api/embotellado', {
method: 'POST',
headers: {
'Content-Type': 'application/json',
'Authorization': `Bearer ${idToken}`,
},
body: JSON.stringify(datosParaEnviar),
});
clearInterval(interval);
setProgress(100);
const data = await response.json();
if (!response.ok) throw new Error(data.error || 'Ocurrió un error.');
setMessage({ type: 'success', text: data.mensaje });
const match = data.mensaje.match(/ID\s+(\S+)/);
invalidarCacheColecciones();
if (match && match[1]) {
const id = match[1];
setTimeout(() => {
navigate(`/trazabilidad/${id}`);
}, 800);
} else {
setIsLoading(false);
setProgress(0);
}
} catch (error) {
setMessage({ type: 'error', text: error.message });
setIsLoading(false);
setProgress(0);
}
};
    const esFormularioValido = () => {
        const { pertenencia, tipo_uso, tipo_vino, profesional_responsable } = formData;
        
        // Se agregó la validación de tipo_uso
        if (!pertenencia || !tipo_uso || !tipo_vino || !profesional_responsable) {
            return false;
        }

        // Validación de que al menos una partida tenga ID y cantidad. Una partida
        // ya embotellada no pide cantidad acá: sale de la columna de botellas.
        const tienePartidasMinimas = formData.partidas_usadas.some(p =>
        p.partida_id && (p.ya_embotellado || (p.cantidad_hl && parseFloat(p.cantidad_hl) > 0))
        );
        if (!tienePartidasMinimas) {
        return false;
        }

        // Si todo el vino es comprado no hay cosecha de dónde sacar el año: el
        // campo "Año del vino" pasa a ser obligatorio (si no, el backend termina
        // usando el año actual como año de cosecha, que no es un dato real).
        if (todoComprado && !(formData.anada && parseInt(formData.anada) > 0)) {
        return false;
        }

        // No dejar pedir mas de lo disponible. El backend lo rechaza igual, pero
        // asi el boton queda deshabilitado antes de mandar el formulario.
        const sePasaDeStock = formData.partidas_usadas.some(p => {
        if (!p.partida_id) return false;
        if (p.ya_embotellado) {
            return p.unidades_disponibles !== undefined && totalBotellasCompradas > p.unidades_disponibles;
        }
        return p.hectolitros_disponibles !== undefined &&
            parseFloat(p.cantidad_hl || 0) > parseFloat(p.hectolitros_disponibles);
        });
        if (sePasaDeStock) {
        return false;
        }

        // Validación de que al menos un embotellado tenga datos completos. Si el
        // vino ya viene embotellado, esa fila no elige botella ni tapón.
        const tieneEmbotelladosMinimos = formData.embotellados.some(b =>
        hayPartidaEmbotellada
            ? b.medida_botella && b.cantidad_botellas && parseInt(b.cantidad_botellas) > 0
            : b.forma_botella && b.medida_botella && b.tipo_corcho && b.cantidad_botellas && parseInt(b.cantidad_botellas) > 0
        );
        if (!tieneEmbotelladosMinimos) {
            return false;
        }

        // No dejar pedir mas botellas o tapones de los que hay en stock. No aplica
        // si el vino ya viene embotellado: ahí no se elige botella ni tapón.
        if (!hayPartidaEmbotellada) {
        const idsUsados = new Set();
        formData.embotellados.forEach(b => {
            if (b.forma_botella) idsUsados.add(b.forma_botella);
            if (b.tipo_corcho) idsUsados.add(b.tipo_corcho);
        });
        const seExcedeBotellasOTapones = Array.from(idsUsados).some(id => {
            const insumo = buscarInsumo(id);
            return insumo && totalPedidoDeItem(id) > Number(insumo.cantidadUnidades || 0);
        });
        if (seExcedeBotellasOTapones) {
            return false;
        }
        }

        return true;
    };

const handlePartidaFieldChange = (index, field, value) => {
setFormData(prev => {
const nuevasPartidas = [...prev.partidas_usadas];
nuevasPartidas[index] = { ...nuevasPartidas[index], [field]: value };
if (field === 'partida_id') {
if ((nuevasPartidas[index].origen || 'vinificacion') === 'stock') {
const insumo = insumos.find(i => i.id === value);
if (insumo) {
nuevasPartidas[index].hectolitros_disponibles = insumoAHectolitros(insumo);
// Si el vino ya viene embotellado, el disponible se compara en botellas
// directamente (cantidadUnidades tal cual está cargado), no en hl.
nuevasPartidas[index].unidades_disponibles = Number(insumo.cantidadUnidades) || 0;
// La cepa se saca del nombre del artículo comprado ("Vino Malbec" -> "Malbec"):
// se confía en que Leandro lo cargue bien, no se pide a mano.
nuevasPartidas[index].cepa = cepaDesdeNombreVino(insumo);
// La bodega se precarga con el proveedor de la compra, pero es editable:
// a veces se compra por un distribuidor y la bodega es otra.
const prov = proveedores[insumo.proveedorAsociado];
if (prov && !nuevasPartidas[index].bodega) {
nuevasPartidas[index].bodega = prov.razonSocial || '';
}
}
} else {
const partidaSeleccionada = partidas.find(p => p.id === value);
if (partidaSeleccionada) {
nuevasPartidas[index].hectolitros_disponibles = partidaSeleccionada.hectolitros_presentes;
}
}
}
return { ...prev, partidas_usadas: nuevasPartidas };
});
};
// cantidad_hl y hectolitros_disponibles se guardan/reciben siempre en hl; esto solo traduce para mostrar/cargar.
// El redondeo a 6 decimales es para pisar el ruido de punto flotante de
// valorHl * 100 (ej. 0.07 * 100 = 7.000000000000001 en JS), no para limitar
// la precisión real del dato.
const hlAUnidadMostrada = (valorHl) => {
if (valorHl === null || valorHl === undefined || valorHl === '') return '';
return unidadVolumen === 'Litros' ? Math.round(valorHl * 100 * 1e6) / 1e6 : valorHl;
};
const handleCantidadHlChange = (index, valorMostrado) => {
if (valorMostrado === '') {
handlePartidaFieldChange(index, 'cantidad_hl', '');
return;
}
const numero = parseFloat(valorMostrado);
if (isNaN(numero)) return;
handlePartidaFieldChange(index, 'cantidad_hl', unidadVolumen === 'Litros' ? numero / 100 : numero);
};
// Cambiar el origen limpia lo elegido: un ID de partida no existe entre los
// insumos ni al revés, así que dejarlo sería un dato inválido.
const handleOrigenChange = (index, origen) => {
setFormData(prev => {
const nuevas = [...prev.partidas_usadas];
nuevas[index] = { origen, partida_id: '', cantidad_hl: '' };
return { ...prev, partidas_usadas: nuevas };
});
setQuery('');
};
// Vino comprado que ya viene embotellado de fábrica (se compra para poner
// marca/etiqueta y vender, no para embotellar acá): no consume botellas ni
// tapones de stock. La cantidad y la medida se cargan en la columna de la
// derecha (sin elegir botella ni tapón), no acá en la partida.
const handleYaEmbotelladoChange = (index, checked) => {
setFormData(prev => {
const nuevas = [...prev.partidas_usadas];
nuevas[index] = {
...nuevas[index],
ya_embotellado: checked,
cantidad_hl: '',
};
return { ...prev, partidas_usadas: nuevas };
});
};
const handleAgregarPartida = () => {
setFormData(prev => ({
...prev,
partidas_usadas: [...prev.partidas_usadas, { origen: 'vinificacion', partida_id: '', cantidad_hl: '' }]
}));
};
const handleEliminarPartida = (index) => {
setFormData(prev => {
const nuevasPartidas = prev.partidas_usadas.filter((_, i) => i !== index);
return { ...prev, partidas_usadas: nuevasPartidas };
});
};
const handleEmbotelladoChange = (index, campo, value) => {
const newValue = value !== undefined ? value : '';
setFormData(prev => {
const nuevos = [...prev.embotellados];
nuevos[index] = {
...nuevos[index],
[campo]: newValue,
};
return { ...prev, embotellados: nuevos };
});
};
const handleAgregarBotella = () => {
setFormData(prev => ({
...prev,
embotellados: [
...prev.embotellados,
{ forma_botella: '', medida_botella: '', tipo_corcho: '', cantidad_botellas: '' }
]
}));
};
const handleEliminarBotella = (index) => {
setFormData(prev => {
const nuevos = prev.embotellados.filter((_, i) => i !== index);
return { ...prev, embotellados: nuevos };
});
};
const opcionesPertenencia = {
'propia': 'Propia',
'tercero': 'Tercero'
};
return (
<div className="container">
<div className="formtitle-container">
<div className="formicon-container">
<EmbotelladoIcon className="formicon" />
</div>
<h1 className="formtitle">Embotellado</h1>
</div>
<hr className="divider" />
<form onSubmit={handleSubmit} className="form">
<div className="columns-container">
<div className="column long">
<div className="input-group">
<Listbox
value={formData.pertenencia}
onChange={(value) => handleChange(null, 'pertenencia', value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${formData.pertenencia ? 'selected' : 'placeholder'}`}>
{opcionesPertenencia[formData.pertenencia] || "Tipo de propiedad"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
<ListboxOption value="propia" className="custom-listbox-option">Propia</ListboxOption>
<ListboxOption value="tercero" className="custom-listbox-option">Tercero</ListboxOption>
</ListboxOptions>
</>
)}
</Listbox>
</div>
{formData.pertenencia === 'tercero' && (
    <div className="input-group">
        <input
            type="text"
            name="nombre_empresa"
            value={formData.nombre_empresa}
            // Reutilizamos la función handleChange existente
            onChange={(e) => handleChange(e, 'nombre_empresa')}
            className="input"
            placeholder="Nombre de la empresa"
        />
    </div>
)}
<div className="input-group">
<Listbox
value={formData.tipo_vino}
onChange={(value) => handleChange(null, 'tipo_vino', value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${
formData.tipo_vino ? 'selected' : 'placeholder'
}`}>
{formData.tipo_vino || "Tipo de vino"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options" style={{maxHeight:'200px'}}>
{tiposDeVino.map((tipo) => (
<ListboxOption
key={tipo}
value={tipo}
className="custom-listbox-option"
>
{tipo}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
<div className="input-group">
<Listbox value={unidadVolumen} onChange={setUnidadVolumen}>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} selected`}>
{unidadVolumen}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{unidadesVolumen.map((opcion) => (
<ListboxOption key={opcion} value={opcion} className="custom-listbox-option">
{opcion}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
{formData.partidas_usadas.map((p, index) => (
<div key={index} className='partida-block'>
<div className="input-group">
<Listbox
value={p.origen || 'vinificacion'}
onChange={(value) => handleOrigenChange(index, value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} selected`}>
{(p.origen || 'vinificacion') === 'stock' ? 'Vino comprado' : 'Partida propia'}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
<ListboxOption value="vinificacion" className="custom-listbox-option">Partida propia</ListboxOption>
<ListboxOption value="stock" className="custom-listbox-option">Vino comprado</ListboxOption>
</ListboxOptions>
</>
)}
</Listbox>
</div>
{(p.origen || 'vinificacion') === 'stock' && (
<label className="checkbox-label">
<input
type="checkbox"
checked={!!p.ya_embotellado}
onChange={(e) => handleYaEmbotelladoChange(index, e.target.checked)}
className="checkbox-input"
/>
<span className="checkbox-checkmark"></span>
Ya en botella
</label>
)}
<div className="input-group">
<Combobox
value={p.partida_id}
onChange={(value) => handlePartidaFieldChange(index, 'partida_id', value)}
>
{({ open }) => (
<>
{}
<ComboboxButton as="div" className={`custom-listbox-button ${open ? 'open' : ''} ${
p.partida_id || query ? 'selected' : 'placeholder'
}`} style={{paddingLeft:'0',paddingRight:'0'}}>
{}
<ComboboxInput
style={{
flexGrow: 1,
padding: '0',
marginLeft:'15px',
border: 'none',
outline: 'none',
backgroundColor: 'transparent',
}}
displayValue={(partidaId) => partidaId || query}
onChange={(event) => setQuery(event.target.value)}
placeholder={(p.origen || 'vinificacion') === 'stock' ? "Seleccionar o buscar vino comprado" : "Seleccionar o buscar partida"}
/>
<span className="custom-arrow-container" style={{marginRight:'7px'}}>
<span className="custom-arrow"></span>
</span>
</ComboboxButton>
<ComboboxOptions className="custom-listbox-options">
{(p.origen || 'vinificacion') === 'stock'
? vinosComprados
.filter(i => nombreInsumo(i).toLowerCase().includes(query.toLowerCase()))
.map((insumo) => (
<ComboboxOption key={insumo.id} value={insumo.id} className="custom-listbox-option">
{nombreInsumo(insumo)}
<span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
{proveedores[insumo.proveedorAsociado]?.razonSocial || 'Proveedor sin datos'} · {insumoAHectolitros(insumo).toLocaleString('es-AR')} hl
</span>
</ComboboxOption>
))
: partidas
.filter(partida =>
partida.estado === 'finalizado' &&
partida.id.toLowerCase().includes(query.toLowerCase())
)
.map((partida) => (
<ComboboxOption key={partida.id} value={partida.id} className="custom-listbox-option">
{partida.id}
</ComboboxOption>
))
}
</ComboboxOptions>
</>
)}
</Combobox>
</div>
{p.ya_embotellado ? (
<>
{/* La cantidad de este vino ya embotellado no se pide acá: sale de la
    columna de botellas (suma de todas las filas). Acá sólo se avisa si
    esa suma se pasa de lo disponible en stock. */}
{p.unidades_disponibles !== undefined && (
<span className="data-label" style={{margin:'0px 10px', flexDirection:'row', display:'flex',alignItems:'center', gap: '5px'}} >
Botellas disponibles: {p.unidades_disponibles.toLocaleString('es-AR')}
</span>
)}
{p.unidades_disponibles !== undefined &&
totalBotellasCompradas > p.unidades_disponibles && (
<span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 10px' }}>
Superaste el stock disponible.
</span>
)}
</>
) : (
<>
{p.hectolitros_disponibles !== undefined && (
<span className="data-label" style={{margin:'0px 10px', flexDirection:'row', display:'flex',alignItems:'center', gap: '5px'}} >
{unidadVolumen === 'Litros' ? 'Litros' : 'Hectolitros'} disponibles: {hlAUnidadMostrada(p.hectolitros_disponibles)}
</span>
)}
{p.hectolitros_disponibles !== undefined &&
parseFloat(p.cantidad_hl || 0) > parseFloat(p.hectolitros_disponibles) && (
<span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 10px' }}>
Superaste el stock disponible.
</span>
)}
<div className="input-group number">
<input
type="number"
value={hlAUnidadMostrada(p.cantidad_hl)}
onChange={(e) => handleCantidadHlChange(index, e.target.value)}
className="input"
placeholder={`Cantidad de ${unidadVolumen === 'Litros' ? 'litros' : 'hectolitros'} a utilizar`}
/><span className='unit'>{abreviaturasVolumen[unidadVolumen]}</span>
</div>
</>
)}
{(p.origen || 'vinificacion') === 'stock' && (
<>
{/* Un vino comprado no tiene viticultura detrás. La cepa sale sola del
    nombre del artículo (ver cepaDesdeNombreVino); sólo la bodega se
    puede ajustar a mano, por si el proveedor fue un distribuidor. */}
{p.cepa && (
<span className="data-label" style={{margin:'0px 10px', flexDirection:'row', display:'flex',alignItems:'center', gap: '5px'}}>
Cepa: {p.cepa}
</span>
)}
<div className="input-group">
<input
type="text"
value={p.bodega || ''}
onChange={(e) => handlePartidaFieldChange(index, 'bodega', e.target.value)}
className="input"
placeholder="Bodega de origen"
/>
</div>
</>
)}
{formData.partidas_usadas.length > 1 && (
<button type="button" onClick={() => handleEliminarPartida(index)} className="remove-btn">
Eliminar
</button>
)}
</div>
))}
{
// Blend no es la única razón para usar más de una partida: un varietal
// (ej. Malbec) puede salir de dos partidas de la misma cepa pero de
// origen distinto (dos lotes, dos bodegas). El mínimo de 2 partidas
// sigue siendo obligatorio solo para "White Blend"/"Red Blend"
// (ver handleSubmit), pero agregar más de una partida está permitido
// para cualquier tipo de vino.
<div type="button" onClick={handleAgregarPartida} className="add-btn-container">
<span className="add-icon">+</span>
<span className="add-txt">Agregar otra partida</span>
</div>
}
                        <div className="input-group">
                            <Listbox
                                value={formData.tipo_uso}
                                onChange={(value) => handleChange(null, 'tipo_uso', value)}
                            >
                                {({ open }) => (
                                    <>
                                        <ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${
                                            formData.tipo_uso ? 'selected' : 'placeholder'
                                            }`}>
                                            {formData.tipo_uso || "Tipo de uso"}
                                            <span className="custom-arrow-container">
                                                <span className="custom-arrow"></span>
                                            </span>
                                        </ListboxButton>
                                        <ListboxOptions className="custom-listbox-options">
                                            {opcionesTipoUso.map((opcion) => (
                                                <ListboxOption
                                                    key={opcion}
                                                    value={opcion}
                                                    className="custom-listbox-option"
                                                >
                                                    {opcion}
                                                </ListboxOption>
                                            ))}
                                        </ListboxOptions>
                                    </>
                                )}
                            </Listbox>
                        </div>
</div>
<div className="column long">
{formData.embotellados.map((b, index) => {
const botellaElegida = buscarInsumo(b.forma_botella);
const taponElegido = buscarInsumo(b.tipo_corcho);
return (
<div key={index} className='botella-block'>
{!hayPartidaEmbotellada && (
<div className="input-group">
<Listbox
value={b.forma_botella}
onChange={(value) => handleEmbotelladoChange(index, 'forma_botella', value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${
b.forma_botella ? 'selected' : 'placeholder'
}`}>
{(botellaElegida ? nombreInsumo(botellaElegida) : b.forma_botella) || "Botella"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{botellasDisponibles.map((insumo) => (
<ListboxOption key={insumo.id} value={insumo.id} className="custom-listbox-option">
{nombreInsumo(insumo)}
<span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
{Number(insumo.cantidadUnidades || 0).toLocaleString('es-AR')} u.
</span>
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
)}
<div className="input-group">
<Listbox
value={b.medida_botella}
onChange={(value) => handleEmbotelladoChange(index, 'medida_botella', value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${
b.medida_botella ? 'selected' : 'placeholder'
}`}>
{b.medida_botella || "Medida de la botella"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{tiposDeMedida.map((medida) => (
<ListboxOption key={medida} value={medida} className="custom-listbox-option">
{medida}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
{!hayPartidaEmbotellada && (
<div className="input-group">
<Listbox
value={b.tipo_corcho}
onChange={(value) => handleEmbotelladoChange(index, 'tipo_corcho', value)}
>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${
b.tipo_corcho ? 'selected' : 'placeholder'
}`}>
{(taponElegido ? nombreInsumo(taponElegido) : b.tipo_corcho) || "Tapón"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{taponesDisponibles.map((insumo) => (
<ListboxOption key={insumo.id} value={insumo.id} className="custom-listbox-option">
{nombreInsumo(insumo)}
<span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
{Number(insumo.cantidadUnidades || 0).toLocaleString('es-AR')} u.
</span>
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
)}
<div className="input-group number">
<input
type="number"
value={b.cantidad_botellas}
onChange={(e) => handleEmbotelladoChange(index, 'cantidad_botellas', e.target.value)}
className="input"
placeholder="Cantidad de botellas"
/>
</div>
{!hayPartidaEmbotellada && (botellaElegida || taponElegido) && (
<span className="data-label" style={{margin:'0px 10px', flexDirection:'row', display:'flex',alignItems:'center', gap: '5px'}}>
Disponible: {botellaElegida ? `${Number(botellaElegida.cantidadUnidades || 0).toLocaleString('es-AR')} botellas` : ''}
{botellaElegida && taponElegido ? ' · ' : ''}
{taponElegido ? `${Number(taponElegido.cantidadUnidades || 0).toLocaleString('es-AR')} tapones` : ''}
</span>
)}
{!hayPartidaEmbotellada && botellaElegida && totalPedidoDeItem(botellaElegida.id) > Number(botellaElegida.cantidadUnidades || 0) && (
<span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 10px' }}>
Superaste el stock disponible de botellas.
</span>
)}
{!hayPartidaEmbotellada && taponElegido && totalPedidoDeItem(taponElegido.id) > Number(taponElegido.cantidadUnidades || 0) && (
<span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 10px' }}>
Superaste el stock disponible de tapones.
</span>
)}
{formData.embotellados.length > 1 && (
<button type="button" onClick={() => handleEliminarBotella(index)} className="remove-btn">
Eliminar
</button>
)}
</div>
);
})}
{!hayPartidaEmbotellada && (
<div type="button" onClick={handleAgregarBotella} className="add-btn-container">
<span className="add-icon">+</span><span className="add-txt">Agregar otro tipo de botella</span>
</div>
)}
{todoComprado && (
<div className="input-group">
{/* Todo el vino es comprado, así que no hay cosecha de dónde sacar el año.
    Con una sola partida propia este campo desaparece y manda su cosecha.
    Obligatorio en este caso (ver esFormularioValido): sin esto el backend
    caía en usar el año actual como si fuera la cosecha. */}
<input
type="number"
value={formData.anada}
onChange={(e) => handleChange(e, 'anada')}
className="input"
placeholder="Año del vino (obligatorio)"
min="1900"
max="2100"
/>
</div>
)}
</div>
<div className="column" style={{minHeight:'100%'}}>
<label className="input-label">Estiba</label>
<div className="input-group">
<label className='date-label'>Fecha de embotellado</label>
<FechaDateInput
name="estiba_inicio"
value={formData.estiba_inicio}
onChange={(iso) => handleChange(null, 'estiba_inicio', iso)}
className="input"
placeholder="Inicio de estiba"
/>
</div>
<div className="input-group">
<label className='date-label'>Fecha de salida al mercado</label>
<FechaDateInput
name="estiba_final"
value={formData.estiba_final} 
onChange={(iso) => handleChange(null, 'estiba_final', iso)}
className="input"
placeholder="Final de estiba"
/>
</div>
<div className="input-group">
<input
type="text"
name="profesional_responsable"
value={formData.profesional_responsable}
onChange={(e) => handleChange(e, 'profesional_responsable')}
className="input"
placeholder="Profesional responsable"
/>
</div>
<div className="button-container" style={{marginTop:'175px'}}> 
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button
type="submit"
className={`button ${message?.type === 'success' ? 'success' : ''}`}
disabled={isLoading || !esFormularioValido()}
>
<span className="button-text">
{message?.type === 'success' ? 'Guardado' : isLoading ? 'Creando...' : 'Crear tirada'}
</span>
{isLoading && (
<span
className="progress-bar"
style={{ width: `${progress}%` }}
></span>
)}
</button>
</div>
</div>
</div>
</form>
{message && (
<div className={`message ${message.type}`}>
{message.text}
</div>
)}
</div>
);
};
export default EmbotelladoForm;